import React, { useEffect, useState, useRef, useCallback } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay } from "swiper/modules";
import styles from "./InstagramFeed.module.css";
import "swiper/css";
import "swiper/css/autoplay";

const InstagramFeed = () => {
  const [posts, setPosts] = useState([]);
  const [featuredPost, setFeaturedPost] = useState(null);
  const [error, setError] = useState("");
  const [scrollY, setScrollY] = useState(0); // Track scroll position
  
  const sectionRef = useRef(null);

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        // Fetch Instagram feed data from your WordPress REST API endpoint.
        const response = await axios.get("/wp-json/custom/v1/instagram-feed");
        const data = response.data;
        
        if (data.posts && data.posts.length > 0) {
          setFeaturedPost(data.featured_post || data.posts[0]);
          setPosts(data.posts);
        }
      } catch (error) {
        console.error("Error fetching Instagram posts:", error);
        setError("Failed to load Instagram posts. Please try again later.");
      }
    };

    fetchPosts();
  }, []);

  // Scroll event handler
  const handleScroll = useCallback(() => {
    setScrollY(window.scrollY);
  }, []);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [handleScroll,scrollY]);

  // Function to check if section is in view
  const isInView = useCallback(() => {
    if (!sectionRef.current) return false;
    const rect = sectionRef.current.getBoundingClientRect();
    return rect.top < window.innerHeight * 0.6 && rect.bottom > 0;
  }, []);

  const handleSlideChange = (swiper) => {
    setTimeout(() => {
      setFeaturedPost(posts[swiper.realIndex]);
    }, 100);
  };

  if (error) return <p className={styles.error}>{error}</p>;

  return (
    <motion.div ref={sectionRef} className={styles.feedContainer}>
      {/* Left: Featured Post */}
      {featuredPost && (
        <motion.div
          className={styles.featuredPost}
          initial={{ opacity: 0, y: 50, scale: 0.9 }}
          animate={isInView() ? { opacity: 1, y: 0, scale: 1 } : {}}
          transition={{ duration: 0.8, ease: "easeOut" }}
        >
          <motion.div
            className={styles.featuredCard}
            initial={{ clipPath: "polygon(0% 0%, 0% 0%, 0% 100%, 0% 100%)", opacity: 0 }}
            animate={isInView() ? { clipPath: "polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)", opacity: 1 } : {}}
            transition={{ duration: 0.8, ease: "easeOut", delay: 0.3 }}
            key={featuredPost.id}
          >
            <img src={featuredPost.media_url} alt="Instagram Post" className={styles.featuredImage} />
            <div className={styles.postDetails}>
              <h4 className={styles.postTitle}>Capturing Your Timeless Moments</h4>
              <a href={featuredPost.permalink} target="_blank" rel="noopener noreferrer" className={styles.circleArrowButton}>
                →
              </a>
            </div>
          </motion.div>
        </motion.div>
      )}

      {/* Right: Swiper Carousel */}
      <motion.div
        className={styles.rightSection}
        initial={{ opacity: 0, y: 50 }}
        animate={isInView() ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.6, ease: "easeOut" }}
      >
        <Swiper
          modules={[Autoplay]}
          spaceBetween={10}
          slidesPerView={posts.length < 3 ? posts.length : 3}
          loop={posts.length > 3}
          autoplay={{ delay: 3000, disableOnInteraction: false }}
          speed={1000}
          onRealIndexChange={handleSlideChange}
          className={styles.carouselContainer}
        >
          {posts.map((post, index) => (
            <SwiperSlide key={index} className={styles.carouselItem}>
              <motion.div
                className={styles.maskTransition}
                initial={{ clipPath: "polygon(0% 0%, 0% 0%, 0% 100%, 0% 100%)", opacity: 0 }}
                animate={isInView() ? { clipPath: "polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)", opacity: 1 } : {}}
                transition={{ duration: 0.6, ease: "easeOut", delay: index * 0.1 }}
              >
                <img src={post.media_url} alt="Instagram Post" className={styles.carouselImage} />
              </motion.div>
              <a href={post.permalink} target="_blank" rel="noopener noreferrer" className={styles.circleArrowButton}>
                →
              </a>
            </SwiperSlide>
          ))}
        </Swiper>
        {/* CTA Section */}
        <div className={styles.descriptionSection}>
          <h4 className={styles.sectionTitle}>Exclusive Studio Previews</h4>
          <p className={styles.sectionText}>
            <strong>Behind the Lens</strong> – Explore our <strong>latest projects, client sessions, and artistic creations</strong>  
            straight from our studio.
          </p>
          <div className={styles.instagramButton}>
            <a href="https://www.instagram.com/maa_mangala_studio/" target="_blank" rel="noopener noreferrer">
              Explore on Instagram →
            </a>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default InstagramFeed;
